import { FilterObject } from '@common/types/queryState';
import {
    FacetPresetGroup,
    FacetRenderItem,
    FacetSortOrder,
} from '@features/facetingEngine/types';
import {
    getNormalizedFilterId,
    isFilterActive,
    isMissingId,
    normalizeBooleanPreset,
} from '@features/facetingEngine/utils/facetLogic';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAggregatedFacets } from './useAggregatedFacets';
import { useFacetController } from './useFacetController';
import { FACET_MISSING_KEY } from '../constants';

const isPresetGroup = (preset: unknown): preset is FacetPresetGroup => {
    return typeof preset === 'object' && preset !== null && 'values' in preset;
};

export const useBooleanFacetItems = (facetKey: string) => {
    const { t } = useTranslation('facetingPanel');
    const { config, value, onChange, getLabel } =
        useFacetController<'boolean'>(facetKey);
    const { groupsById, normalizer, missingCounts } =
        useAggregatedFacets(facetKey);

    const resolveSingleFilter = useCallback(
        (filter: FilterObject, customLabel?: string): FacetRenderItem => {
            const id = getNormalizedFilterId(filter, normalizer);
            const group = groupsById[id];

            const filters =
                group && !isMissingId(id)
                    ? group.rawKeys.map((rk) => ({
                          value: rk,
                          operator: filter.operator,
                      }))
                    : [filter];

            const label = isMissingId(id)
                ? t(FACET_MISSING_KEY)
                : customLabel || filter.label || getLabel(id);

            return { id, filters, label };
        },
        [normalizer, groupsById, t, getLabel],
    );

    const resolveGroupPreset = useCallback(
        (preset: FacetPresetGroup): FacetRenderItem => {
            const allFilters = preset.values.flatMap(
                (v) =>
                    resolveSingleFilter(
                        normalizeBooleanPreset(v as string | FilterObject),
                    ).filters,
            );

            return {
                id: `preset-group-${preset.key}`,
                label: getLabel(preset.key),
                filters: allFilters,
            };
        },
        [getLabel, resolveSingleFilter],
    );

    const getCount = useCallback(
        (item: FacetRenderItem) => {
            const rawIds = item.filters.map((f) =>
                getNormalizedFilterId(f, normalizer),
            );
            const uniqueIds = Array.from(new Set(rawIds));

            return uniqueIds.reduce(
                (acc, id) => {
                    const counts = isMissingId(id)
                        ? missingCounts
                        : groupsById[id] || { total: 0, matches: 0 };

                    return {
                        total: acc.total + counts.total,
                        matches: acc.matches + counts.matches,
                    };
                },
                { total: 0, matches: 0 },
            );
        },
        [normalizer, groupsById, missingCounts],
    );

    const sortHasMatches = useCallback(
        (a: FacetRenderItem, b: FacetRenderItem): number => {
            const hasMatchesA = getCount(a).matches > 0 ? 1 : 0;
            const hasMatchesB = getCount(b).matches > 0 ? 1 : 0;
            return hasMatchesB - hasMatchesA;
        },
        [getCount],
    );

    const sortAlpha = useCallback(
        (a: FacetRenderItem, b: FacetRenderItem): number =>
            getLabel(a.label).localeCompare(getLabel(b.label)),
        [getLabel],
    );

    const sortCount = useCallback(
        (a: FacetRenderItem, b: FacetRenderItem): number =>
            getCount(b).total - getCount(a).total,
        [getCount],
    );

    const sortChips = useCallback(
        (
            a: FacetRenderItem,
            b: FacetRenderItem,
            options: { sticky?: boolean; defaultOrder?: FacetSortOrder } = {},
        ): number => {
            if (isMissingId(a.id)) return -1;
            if (isMissingId(b.id)) return 1;

            const { sticky = false, defaultOrder = 'count' } = options;
            const order = config?.sortOrder || defaultOrder;

            if (!sticky) {
                const hasMatches = sortHasMatches(a, b);
                if (hasMatches !== 0) return hasMatches;
            }

            if (order === 'count') return sortCount(a, b);
            return sortAlpha(a, b);
        },
        [config?.sortOrder, sortAlpha, sortCount, sortHasMatches],
    );

    const { sortedPresets, sortedExtras } = useMemo(() => {
        if (!config) return { sortedPresets: [], sortedExtras: [] };

        const strictPresets: FacetRenderItem[] = (config.presets ?? []).map(
            (p) => {
                if (isPresetGroup(p)) return resolveGroupPreset(p);
                return resolveSingleFilter(normalizeBooleanPreset(p));
            },
        );

        const presetIds = new Set(
            strictPresets.reduce<string[]>(
                (acc, preset) => [
                    ...acc,
                    preset.id,
                    ...preset.filters.map((f) =>
                        getNormalizedFilterId(f, normalizer),
                    ),
                ],
                [],
            ),
        );

        const extras: FacetRenderItem[] = Object.keys(groupsById)
            .filter((id) => !isMissingId(id) && !presetIds.has(id))
            .map((id) => resolveSingleFilter({ value: id, operator: 'exact' }));

        return {
            sortedPresets: strictPresets.sort((a, b) =>
                sortChips(a, b, { sticky: true, defaultOrder: 'count' }),
            ),
            sortedExtras: extras.sort((a, b) =>
                sortChips(a, b, { sticky: false, defaultOrder: 'count' }),
            ),
        };
    }, [
        config,
        groupsById,
        normalizer,
        resolveGroupPreset,
        resolveSingleFilter,
        sortChips,
    ]);

    const checkIsActive = useCallback(
        (item: FacetRenderItem) => {
            if (!value || value.length === 0) return false;
            return item.filters.every((f) => isFilterActive(value, f));
        },
        [value],
    );

    const handleToggle = useCallback(
        (item: FacetRenderItem) => {
            const isFullyActive = checkIsActive(item);

            if (isFullyActive) {
                onChange(item.filters);
            } else {
                const filtersToActivate = item.filters.filter(
                    (f) => !isFilterActive(value, f),
                );
                onChange(filtersToActivate);
            }
        },
        [onChange, checkIsActive, value],
    );

    const activeSummaryItems = useMemo(() => {
        return [...sortedPresets, ...sortedExtras]
            .filter(checkIsActive)
            .sort((a, b) => b.filters.length - a.filters.length)
            .reduce<{
                badges: FacetRenderItem[];
                coveredKeys: ReadonlySet<string>;
            }>(
                (acc, item) => {
                    const itemKeys = item.filters.map(
                        (f) =>
                            `${getNormalizedFilterId(f, normalizer)}_${f.operator}`,
                    );

                    const isFullyCovered =
                        itemKeys.length > 0 &&
                        itemKeys.every((k) => acc.coveredKeys.has(k));

                    if (isFullyCovered) {
                        return acc;
                    }

                    return {
                        badges: [...acc.badges, item],
                        coveredKeys: new Set([...acc.coveredKeys, ...itemKeys]),
                    };
                },
                { badges: [], coveredKeys: new Set() },
            ).badges;
    }, [sortedPresets, sortedExtras, checkIsActive, normalizer]);

    const handleGetLabel = useCallback((item: FacetRenderItem) => {
        return item.label;
    }, []);

    const handleGetKey = useCallback((item: FacetRenderItem) => {
        return item.id;
    }, []);

    return {
        config,
        sortedPresets,
        sortedExtras,
        activeSummaryItems,
        checkIsActive,
        handleToggle,
        getCount,
        handleGetLabel,
        handleGetKey,
    };
};
